import React, { useCallback, useState } from "react";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { requestIdentityVerification } from "@lib/authentication";
import {
    CodeVerification,
    CodeVerificationProps,
} from "@components/auth/CodeVerification/CodeVerification";
import { useCreateWorkspaceReceiptSenderEmail } from "@api/workspaceReceiptSender.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { AddReceiptSenderForm } from "./AddReceiptSenderForm";

interface AddReceiptSenderModalProps {
    show: boolean;
    onClose: () => void;
}

export const AddReceiptSenderModal: React.FC<AddReceiptSenderModalProps> = ({
    show,
    onClose,
}) => {
    const [email, setEmail] = useState<string>();
    const createSender = useCreateWorkspaceReceiptSenderEmail();
    const { toast, error } = useToaster();

    const requestVerification: CodeVerificationProps["request"] =
        useCallback(async () => {
            if (!email) {
                throw new Error("No email provided");
            }
            await requestIdentityVerification({
                phoneOrEmail: email,
            });
        }, [email]);

    const handleVerificationSuccess: CodeVerificationProps["onSuccess"] =
        useCallback(
            async (verificationCode: string) => {
                if (!email) {
                    return;
                }

                try {
                    await createSender.mutateAsync({
                        email,
                        verificationCode,
                    });
                    toast("Receipt sender added");
                    setEmail(undefined);
                    onClose();
                } catch (err: any) {
                    const message =
                        err?.body?.message || "Failed to add receipt sender";
                    error(message);
                }
            },
            [email, createSender, toast, error, onClose],
        );

    const handleEmailSubmit = useCallback((submittedEmail: string) => {
        setEmail(submittedEmail);
    }, []);

    const handleClose = useCallback(() => {
        setEmail(undefined);
        onClose();
    }, [onClose]);

    return (
        <PlainModal show={show} onHide={handleClose}>
            <StandardModalHeader>
                {email ? "Verify Email" : "Add Receipt Sender"}
            </StandardModalHeader>

            {email ? (
                <CodeVerification
                    phoneOrEmail={email}
                    onSuccess={handleVerificationSuccess}
                    request={requestVerification}
                />
            ) : (
                <AddReceiptSenderForm
                    onSubmit={handleEmailSubmit}
                    onCancel={handleClose}
                />
            )}
        </PlainModal>
    );
};
